export type DifficultyLevel = 'a0' | 'a1' | 'a2' | 'b1' | 'b2' | 'c1' | 'c2'

export interface Reading {
  id: number
  title: string
  body: string | null
  difficulty_level: DifficultyLevel
  source: string | null
  language_id: number
  author_id: number
  created_at: string
  /** Theme names, lower-case and alphabetical. */
  themes: string[]
}

export const DIFFICULTY_LABELS: Record<DifficultyLevel, string> = {
  a0: 'A0 – Beginner',
  a1: 'A1 – Elementary',
  a2: 'A2 – Pre-Intermediate',
  b1: 'B1 – Intermediate',
  b2: 'B2 – Upper Intermediate',
  c1: 'C1 – Advanced',
  c2: 'C2 – Mastery',
}

export const DIFFICULTY_LEVELS: DifficultyLevel[] = ['a0', 'a1', 'a2', 'b1', 'b2', 'c1', 'c2']

/**
 * Lifecycle of one "Fetch Linguistic Data" run. queued/running are non-terminal.
 *
 * `cancelled` is distinct from `failed`: an admin who stopped a run on purpose should
 * not be shown an error. A run being *asked* to stop is not a status — it stays
 * `running` with `cancel_requested_at` set, because it works until its next word.
 */
export type EnrichmentRunStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'

/**
 * One admin-triggered run that pre-warms a reading's vocabulary.
 * Contract: specs/006-fetch-linguistic-data/contracts/enrichment-runs-api.md
 */
export interface EnrichmentRun {
  id: number
  status: EnrichmentRunStatus
  total_words: number
  processed_words: number
  enriched_count: number
  skipped_count: number
  unresolved_count: number
  /** Names (people, places, organisations) the run skipped as non-vocabulary. */
  proper_noun_count: number
  error_message: string | null
  /** Set once an admin asked the run to stop; it keeps working until its next word. */
  cancel_requested_at: string | null
  started_at: string | null
  finished_at: string | null
  created_at: string
}

export const ENRICHMENT_RUN_ACTIVE_STATUSES: EnrichmentRunStatus[] = ['queued', 'running']

export function isEnrichmentRunActive(run: EnrichmentRun | null): boolean {
  return run !== null && ENRICHMENT_RUN_ACTIVE_STATUSES.includes(run.status)
}

/** Asked to stop, but still working. Do not offer a button that was already pressed. */
export function isEnrichmentRunStopping(run: EnrichmentRun | null): boolean {
  return isEnrichmentRunActive(run) && run?.cancel_requested_at != null
}

/**
 * Languages the lookup pipeline supports. Mirrors Words::SupportedLanguages::ISO_CODES
 * (Ruby) and MODULES in microservices/nlp/app/routes/lookup.py.
 */
export const ENRICHMENT_SUPPORTED_ISO_CODES: string[] = ['is', 'de', 'es', 'nb']
